import React, { useState } from "react";
import "../landingpage.css";
import { supabase } from "../lib/supabase";

export default function NotesAppLandingPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('download');
    waitlistSection.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus({ type: "", message: "" });

    try {
      // Validate email
      if (!email || !email.includes("@")) {
        throw new Error("Please enter a valid email address");
      }

      // Insert email into Supabase
      const { error } = await supabase
        .from("waitlist")
        .insert([{ email, created_at: new Date().toISOString() }]);

      if (error) {
        // Check if error is due to duplicate email
        if (error.code === '23505' && error.message.includes('waitlist_email_key')) {
          setStatus({
            type: "info",
            message: "You're already on the waitlist! We'll notify you when we launch.",
          });
        } else {
          throw error;
        }
      } else {
        setStatus({
          type: "success",
          message: "Thanks for joining! We'll notify you when we launch.",
        });
      }
      setEmail("");
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <header className="header">
        <h1 className="logo">OpenNotes</h1>
        <button className="mobile-menu-button" onClick={toggleMenu}>
          {isMenuOpen ? '✕' : '☰'}
        </button>
        <nav className={`nav ${isMenuOpen ? 'active' : ''}`}>
          <a href="#features" onClick={() => setIsMenuOpen(false)}>Features</a>
          <a href="#community" onClick={() => setIsMenuOpen(false)}>Community</a>
          <a href="#download" onClick={() => setIsMenuOpen(false)}>Download</a>
        </nav>
      </header>

      <section className="hero">
        <h2>Scroll, Search & Share Notes Like Never Before</h2>
        <p>
          OpenNotes is a social platform that transforms how students discover and share study notes. Think of it as Twitter meets education.
        </p>
        <button className="cta-button" onClick={scrollToWaitlist}>Get Early Access</button>
      </section>

      <section id="features" className="features">
        <h3>Features</h3>
        <div className="features-grid">
          <div className="feature-card">
            <h4>Scrollable Feed</h4>
            <p>Browse notes like you scroll through social media — intuitive, visual, and addictive.</p>
          </div>
          <div className="feature-card">
            <h4>Smart Search</h4>
            <p>Find the exact notes you need with AI-powered search and tagging.</p>
          </div>
          <div className="feature-card">
            <h4>Creator Profiles</h4>
            <p>Follow your favorite note creators and explore trending content.</p>
          </div>
          <div className="feature-card">
            <h4>AI Summarization</h4>
            <p>Get instant summaries of long notes using built-in AI.</p>
          </div>
          <div className="feature-card">
            <h4>Gamified Sharing</h4>
            <p>Earn badges and ranks by uploading valuable content.</p>
          </div>
          <div className="feature-card">
            <h4>Community Driven</h4>
            <p>Engage in discussions, upvote notes, and collaborate in a community that loves learning.</p>
          </div>
        </div>
      </section>

      <section id="community" className="community">
        <h3>Built for Learners, by Learners</h3>
        <p>
          Whether you're preparing for exams, revising before class, or just curious to explore, OpenNotes makes academic content accessible and enjoyable. Join thousands of students transforming how knowledge is shared.
        </p>
        <div className="notify-form">
          <input
            type="email"
            placeholder="Enter your email to join the waitlist"
            className="email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          <button 
            type="submit" 
            className="cta-button"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Join Waitlist"}
          </button>
        </div>
        {status.message && (
          <div className={`status-message ${status.type}`}>
            {status.message}
          </div>
        )}
      </section>

      <footer id="download" className="footer">
        <h4>Coming soon on Android & iOS</h4>
        <p>Get ready to transform how you share and discover study notes</p>
      </footer>
    </div>
  );
}
